/**
 * Symbol library — the core visual vocabulary of the SLD editor.
 *
 * Every electrical component is a SymbolDef: a declaration with
 *   - an SVG content string in native coordinates
 *   - named, electrically-typed terminals
 *   - a default scale for rendering in world coords
 *
 * Symbols are positioned (place()) by the renderer, and rendered as
 * React Flow custom nodes that expose their terminals as Handles.
 */
import type { Terminal, TerminalType } from './topology';

export interface SymbolDef {
  /** Unique identifier (e.g. 'inverter', 'fuse', 'dcIsolator') */
  id: string;
  /** Source — for attribution */
  source: 'upb-lea' | 'handrolled';
  /** Human-friendly name (for palette, legend, tooltips) */
  label: string;
  /** Category (for grouping in palette) */
  category: 'inverter' | 'switchgear' | 'protection' | 'metering' | 'storage' | 'pv' | 'utility' | 'misc';
  /** Native coordinate space of the SVG content */
  nativeBox: { x: 0; y: 0; w: number; h: number };
  /** Default scale when rendering in world coordinates */
  defaultScale: number;
  /** Raw SVG content (children only, no <svg> wrapper) in native coords */
  content: string;
  /** Named, typed connection points in native coordinates */
  terminals: Record<string, Terminal>;
}

export const TYPE_COLORS: Record<TerminalType, string> = {
  'dc+':   '#c62828',
  'dc-':   '#0f172a',
  'dc':    '#0f172a',
  'ac':    '#0f172a',
  'earth': '#16a34a',
  'any':   '#0f172a'
};

const COMPAT: Set<string> = new Set([
  'dc+|dc+', 'dc-|dc-', 'dc|dc', 'ac|ac', 'earth|earth', 'any|any',
  'dc|dc+', 'dc|dc-',
  'any|dc+', 'any|dc-', 'any|dc', 'any|ac', 'any|earth'
]);

export function typesCompatible(a: TerminalType, b: TerminalType): boolean {
  return COMPAT.has(`${a}|${b}`) || COMPAT.has(`${b}|${a}`);
}

/**
 * Pick the most specific terminal type between two endpoints. Used
 * to colour a wire when the user drags between a polarised terminal
 * (dc+, dc-, ac, earth) and an unpolarised one (dc, any). Without
 * this, dragging from inverter→panel yields a different colour than
 * dragging panel→inverter even though the wire is electrically
 * identical.
 *
 * Specificity order (high → low): dc+, dc-, ac, earth > dc > any.
 * If both endpoints are equally specific (dc+ ↔ dc+), either works
 * — we return the source side. If both are unpolarised, return
 * source. The result is what `data.style` is set to on the wire.
 */
const SPECIFICITY: Record<TerminalType, number> = {
  'dc+':   3,
  'dc-':   3,
  'ac':    3,
  'earth': 3,
  'dc':    1,
  'any':   0,
};
export function preferredWireStyle(
  a: TerminalType,
  b: TerminalType
): TerminalType {
  return SPECIFICITY[a] >= SPECIFICITY[b] ? a : b;
}

/**
 * Friendly label for a terminal. Used as the `title` attribute on
 * each connection handle so hovering over a terminal dot reveals
 * what it is — useful for new users (especially in the hybrid
 * inverter case where there are 3 terminals and "the second dot on
 * the right" needs context to make sense).
 *
 * Falls back to TitleCasing the terminal id for any handle whose id
 * isn't in the map (covers user-added or one-off symbols).
 */
const TERMINAL_LABELS: Record<string, string> = {
  dc_in:    'DC Input',
  dc_out:   'DC Output',
  ac_in:    'AC Input',
  ac_out:   'AC Output',
  bat_port: 'Battery Port',
  pos:      'Positive (DC+)',
  neg:      'Negative (DC−)',
  earth:    'Earth',
  gnd:      'Earth',
  pe:       'Earth (PE)',
  // Switchgear terminals — a/b are the two contacts of a switch /
  // isolator / fuse / similar pass-through device.
  a:        'Terminal A',
  b:        'Terminal B',
};

export function terminalTooltip(termId: string, type: TerminalType): string {
  const name =
    TERMINAL_LABELS[termId] ??
    termId.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const typeStr =
    type === 'dc+' ? 'DC+' :
    type === 'dc-' ? 'DC−' :
    type === 'dc'  ? 'DC' :
    type === 'ac'  ? 'AC' :
    type === 'earth' ? 'Earth' :
    'Any';
  return `${name} · ${typeStr}`;
}
